// src/pages/CustomersPage.js
import React, { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import DashboardLayout from "../../components/test/DashboardLayout";
import CustomerFilterBar from "../../components/customer/CustomerFilterBar";
import CustomerTable from "../../components/customer/CustomerTable";
import CustomerProfileModal from "../../components/modals/CustomerProfileModal";
import {useParams} from "react-router-dom";

const CustomersPage = () => {
    const {userId, businessId} = useParams();

    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null); // Will be null for new customer, or an object for existing
    const [searchTerm, setSearchTerm] = useState('');
    const [filterProgram, setFilterProgram] = useState('All');

    // Mock Data for registered customers
    const [customers, setCustomers] = useState([
        {
            id: 'cust-001',
            name: 'Emily Chen',
            email: 'emily.c@example.com',
            phone: '0821234567',
            address: '123 Oak Ave, Johannesburg',
            program: 'Standard Points Program',
            points: 7500,
            tier: 'Gold',
            totalPurchases: 12,
            totalSpend: 1500,
            lastActivity: '2025-05-30',
            status: 'Active',
            rewardsAvailable: 3,
            avatar: 'https://i.pravatar.cc/150?img=3'
        },
        {
            id: 'cust-002',
            name: 'David Lee',
            email: 'david.l@example.com',
            phone: '0719876543',
            address: '456 Pine Rd, Cape Town',
            program: 'VIP Tier Rewards',
            points: 6800,
            tier: 'Silver',
            totalPurchases: 8,
            totalSpend: 1200,
            lastActivity: '2025-05-28',
            status: 'Active',
            rewardsAvailable: 1,
            avatar: 'https://i.pravatar.cc/150?img=5'
        },
        {
            id: 'cust-003',
            name: 'Sarah Miller',
            email: 'sarah.m@example.com',
            phone: '0601112233',
            address: '789 Maple St, Durban',
            program: 'Standard Points Program',
            points: 5900,
            tier: 'Silver',
            totalPurchases: 10,
            totalSpend: 1100,
            lastActivity: '2025-05-25',
            status: 'Active',
            rewardsAvailable: 2,
            avatar: 'https://i.pravatar.cc/150?img=8'
        },
        {
            id: 'cust-004',
            name: 'John Doe',
            email: 'john.d@example.com',
            phone: '0794445566',
            address: '101 Cedar Ln, Pretoria',
            program: 'Seasonal Spend & Save',
            points: 5200,
            tier: 'N/A',
            totalPurchases: 6,
            totalSpend: 800,
            lastActivity: '2025-05-20',
            status: 'Active',
            rewardsAvailable: 0,
            avatar: 'https://i.pravatar.cc/150?img=12'
        },
        {
            id: 'cust-005',
            name: 'Maria Garcia',
            email: 'maria.g@example.com',
            phone: '0837778899',
            address: '222 Birch Ave, Bloemfontein',
            program: 'Standard Points Program',
            points: 4800,
            tier: 'Bronze',
            totalPurchases: 7,
            totalSpend: 950,
            lastActivity: '2025-05-18',
            status: 'Active',
            rewardsAvailable: 1,
            avatar: 'https://i.pravatar.cc/150?img=15'
        },
    ]);

    // Mock list of available loyalty programs for filtering/selection
    const availablePrograms = ['All', 'Standard Points Program', 'VIP Tier Rewards', 'Seasonal Spend & Save'];

    const filteredCustomers = customers.filter(customer => {
        const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customer.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesProgram = filterProgram === 'All' || customer.program === filterProgram;
        return matchesSearch && matchesProgram;
    });

    const handleViewProfile = (customer) => {
        setSelectedCustomer(customer);
        setIsProfileModalOpen(true);
    };

    // This function now handles both adding a new customer and saving edits to an existing one
    const handleSaveCustomer = (customerData) => {
        if (customerData.id) {
            // It's an existing customer, update it
            setCustomers(customers.map(cust => (cust.id === customerData.id ? customerData : cust)));
        } else {
            // It's a new customer, add it
            const newCustomer = {
                ...customerData,
                id: `cust-${Date.now()}`, // Simple unique ID generation
                status: 'Active', // Default status for new customers
                rewardsAvailable: 0,
                totalPurchases: 0,
                totalSpend: 0,
                lastActivity: new Date().toISOString().slice(0, 10), // Current date
                points: customerData.points || 0, // Ensure points are set if not provided by form
                tier: customerData.tier || (customerData.program === 'VIP Tier Rewards' ? 'Bronze' : 'N/A'), // Default tier
                avatar: `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 70) + 1}` // Random avatar
            };
            setCustomers([...customers, newCustomer]);
        }
        setIsProfileModalOpen(false); // Close modal
        setSelectedCustomer(null); // Reset selected customer
    };

    const handleCloseModal = () => {
        setIsProfileModalOpen(false);
        setSelectedCustomer(null); // Always reset when closing
    };

    return (
        <DashboardLayout userId={userId} businessId={businessId}>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Customers</h1>
                <button
                    onClick={() => { setSelectedCustomer(null); setIsProfileModalOpen(true); }} // Set selectedCustomer to null to indicate 'add' mode
                    className="bg-indigo-600 text-white px-5 py-2 rounded-lg flex items-center space-x-2 hover:bg-indigo-700 transition duration-200 shadow-md"
                >
                    <PlusIcon className="h-5 w-5" />
                    <span>Add New Customer</span>
                </button>
            </div>

            <CustomerFilterBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                filterProgram={filterProgram}
                setFilterProgram={setFilterProgram}
                availablePrograms={availablePrograms}
            />

            {customers.length === 0 && searchTerm === '' && filterProgram === 'All' ? ( // Show empty state only if no customers and no filters applied
                <div className="bg-white rounded-lg shadow-md p-8 text-center border-dashed border-2 border-gray-300 mt-6">
                    <p className="text-xl font-semibold text-gray-700 mb-4">No customers registered yet!</p>
                    <p className="text-gray-500 mb-6">Once customers join your loyalty programs, they will appear here. Or you can add them manually.</p>
                    <button
                        onClick={() => { setSelectedCustomer(null); setIsProfileModalOpen(true); }}
                        className="bg-indigo-600 text-white px-6 py-3 rounded-lg flex items-center space-x-2 mx-auto hover:bg-indigo-700 transition duration-200 shadow-lg"
                    >
                        <PlusIcon className="h-6 w-6" />
                        <span>Add Your First Customer</span>
                    </button>
                </div>
            ) : (
                <CustomerTable customers={filteredCustomers} onViewProfile={handleViewProfile} />
            )}

            <CustomerProfileModal
                isOpen={isProfileModalOpen}
                onClose={handleCloseModal}
                customer={selectedCustomer} // This will be null for 'add' mode
                onSave={handleSaveCustomer}
                availablePrograms={availablePrograms.filter(p => p !== 'All')} // Pass programs for selection
            />
        </DashboardLayout>
    );
};

export default CustomersPage;